import React, { useState, useEffect, useRef, useCallback } from "react";
import { io } from "socket.io-client";
import API from "../../api/axiosConfig";
import { toast } from "react-toastify";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/es";
import { getImageUrl } from "../../utils/getImageUrl";
import UserVerificationModal from "../../components/modals/UserVerificationModal";
import UserProfileDrawer, { ROLE_COLORS } from "../../components/admin/UserProfileDrawer";
import {
  Users, Search, Eye, X,
  CheckCircle, XCircle,
  ChevronLeft, ChevronRight, RefreshCw,
  BadgeCheck, Wifi, WifiOff,
} from "lucide-react";

const SOCKET_URL = (
  process.env.REACT_APP_API_URL || "http://localhost:4000/api"
).replace(/\/api\/?$/, "");

dayjs.extend(relativeTime);
dayjs.locale("es");

// ── Badge helpers ─────────────────────────────────────────────────────────────
function CheckBadge({ ok, label }) {
  return ok ? (
    <span className="inline-flex items-center gap-0.5 text-green-600">
      <CheckCircle size={13} /> <span className="text-xs">{label}</span>
    </span>
  ) : (
    <span className="inline-flex items-center gap-0.5 text-gray-300">
      <XCircle size={13} /> <span className="text-xs">{label}</span>
    </span>
  );
}

// ── Main Page ─────────────────────────────────────────────────────────────────
export default function UserClients() {
  const [users, setUsers]           = useState([]);
  const [total, setTotal]           = useState(0);
  const [page, setPage]             = useState(1);
  const [pages, setPages]           = useState(1);
  const [search, setSearch]         = useState("");
  const [loading, setLoading]       = useState(false);
  const [profileId, setProfileId]   = useState(null);
  const [onlineStats, setOnlineStats] = useState(null);
  const [onlyDocuments, setOnlyDocuments] = useState(false);
  const [verifyFilter, setVerifyFilter]   = useState("");
  const [verifyUser, setVerifyUser]       = useState(null);
  const searchTimeout               = useRef(null);
  const LIMIT = 20;

  // Real-time online users via socket
  useEffect(() => {
    const token =
      localStorage.getItem("accessToken") || localStorage.getItem("token");
    if (!token) return;
    const socket = io(SOCKET_URL, { transports: ["websocket"], auth: { token } });
    socket.on("stats:online", (stats) => setOnlineStats(stats));
    return () => socket.disconnect();
  }, []);

  const fetchUsers = useCallback(async (p = 1, q = search) => {
    setLoading(true);
    try {
      const res = await API.get("/users", {
        params: {
          page: p, limit: LIMIT,
          ...(q ? { search: q } : {}),
          ...(onlyDocuments ? { documents: "true" } : {}),
          ...(verifyFilter ? { verification: verifyFilter } : {}),
        },
      });
      const d = res.data?.data ?? {};
      setUsers(d.data ?? []);
      setTotal(d.total ?? 0);
      setPages(d.pages ?? 1);
      setPage(p);
    } catch {
      toast.error("Error al cargar usuarios");
    } finally {
      setLoading(false);
    }
  }, [search, onlyDocuments, verifyFilter]); // eslint-disable-line

  useEffect(() => { fetchUsers(1, ""); }, []); // eslint-disable-line

  const handleSearch = (val) => {
    setSearch(val);
    clearTimeout(searchTimeout.current);
    searchTimeout.current = setTimeout(() => fetchUsers(1, val), 350);
  };

  // Stats derived from current page (backend doesn't send aggregates)
  const stats = {
    total,
    active:   users.filter((u) => u.isActive !== false).length,
    email:    users.filter((u) => u.emailVerified).length,
    whatsapp: users.filter((u) => u.whatsappVerified).length,
  };

  return (
    <div className="w-full max-w-7xl mx-auto px-4 sm:px-0 space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center gap-3">
        <div className="flex-1">
          <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
            <Users size={24} className="text-blue-500" /> Usuarios registrados
          </h2>
          <p className="text-gray-500 text-sm mt-0.5">
            {total.toLocaleString()} usuarios en total
          </p>
        </div>
        <button
          onClick={() => fetchUsers(page, search)}
          className="flex items-center gap-2 px-4 py-2 bg-white border rounded-xl text-gray-600 hover:bg-gray-50 transition text-sm shadow-sm"
        >
          <RefreshCw size={14} className={loading ? "animate-spin" : ""} /> Actualizar
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          value={search}
          onChange={(e) => handleSearch(e.target.value)}
          placeholder="Buscar por nombre, email o teléfono…"
          className="w-full pl-10 pr-4 py-3 bg-white border border-gray-200 rounded-xl text-sm focus:outline-none focus:ring-2 focus:ring-blue-300 shadow-sm"
        />
        {search && (
          <button
            onClick={() => { setSearch(""); fetchUsers(1, ""); }}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600"
          >
            <X size={15} />
          </button>
        )}
      </div>

      {/* Online users — real-time socket stats */}
      {onlineStats !== null && (
        <div className="flex flex-wrap gap-3">
          <div className="flex items-center gap-3 bg-white border border-gray-200 rounded-2xl px-4 py-3 shadow-sm flex-1 min-w-[220px]">
            <span className="relative flex h-3 w-3">
              <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-green-400 opacity-75" />
              <span className="relative inline-flex rounded-full h-3 w-3 bg-green-500" />
            </span>
            <div>
              <p className="text-xs text-gray-400 font-medium">Conectados ahora</p>
              <p className="text-xl font-bold text-gray-800 leading-none mt-0.5">{onlineStats.total}</p>
            </div>
            <div className="ml-auto flex gap-4 text-center">
              <div>
                <p className="text-lg font-bold text-blue-600">{onlineStats.authenticated}</p>
                <p className="text-[10px] text-gray-400">con cuenta</p>
              </div>
              <div>
                <p className="text-lg font-bold text-amber-500">{onlineStats.anonymous}</p>
                <p className="text-[10px] text-gray-400">sin cuenta</p>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Stats Strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        {[
          { label: "Total", value: total, color: "text-gray-800", bg: "bg-gray-50", border: "border-gray-200" },
          { label: "Activos (pág.)", value: stats.active, color: "text-green-700", bg: "bg-green-50", border: "border-green-200" },
          { label: "Email ✓ (pág.)", value: stats.email, color: "text-blue-700", bg: "bg-blue-50", border: "border-blue-200" },
          { label: "WhatsApp ✓ (pág.)", value: stats.whatsapp, color: "text-emerald-700", bg: "bg-emerald-50", border: "border-emerald-200" },
        ].map((s) => (
          <div key={s.label} className={`${s.bg} border ${s.border} rounded-xl p-3 text-center`}>
            <div className={`text-2xl font-bold ${s.color}`}>{s.value}</div>
            <div className="text-xs text-gray-500 mt-0.5">{s.label}</div>
          </div>
        ))}
      </div>

      {/* Filtros de documento */}
      <div className="flex flex-wrap items-center gap-3">
        <button
          onClick={() => { setOnlyDocuments((v) => !v); }}
          className={`px-3 py-2 rounded-xl text-sm font-semibold border transition ${onlyDocuments ? "bg-blue-500 text-white border-blue-500" : "bg-white text-gray-600 border-gray-200 hover:bg-gray-50"}`}
        >
          Solo con documento subido
        </button>
        <select
          value={verifyFilter}
          onChange={(e) => setVerifyFilter(e.target.value)}
          className="px-3 py-2 rounded-xl text-sm border border-gray-200 bg-white text-gray-700"
        >
          <option value="">Toda verificación</option>
          <option value="pendiente">Pendiente de revisión</option>
          <option value="aprobado">Verificado</option>
          <option value="rechazado">Rechazado</option>
        </select>
      </div>

      {/* Table */}
      <div className="bg-white rounded-2xl border border-gray-200 shadow-sm overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="bg-gray-50 border-b text-left">
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Usuario</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide hidden md:table-cell">Teléfono</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Rol</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide hidden lg:table-cell">QPoints</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide hidden sm:table-cell">Verificaciones</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide hidden xl:table-cell">Registro</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Estado</th>
                <th className="px-4 py-3 font-semibold text-gray-500 text-xs uppercase tracking-wide">Acción</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading && users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center">
                    <RefreshCw className="animate-spin mx-auto text-gray-300 mb-2" size={24} />
                    <p className="text-gray-400 text-sm">Cargando…</p>
                  </td>
                </tr>
              ) : users.length === 0 ? (
                <tr>
                  <td colSpan={8} className="py-16 text-center text-gray-400 text-sm">
                    No se encontraron usuarios
                  </td>
                </tr>
              ) : (
                users.map((u) => (
                  <tr key={u.id} className="hover:bg-gray-50/50 transition">
                    {/* Avatar + name/email */}
                    <td className="px-4 py-3">
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-gradient-to-br from-gray-100 to-gray-200 overflow-hidden flex-shrink-0">
                          {u.avatar ? (
                            <img src={getImageUrl(u.avatar)} alt={u.name} className="w-full h-full object-cover" />
                          ) : (
                            <div className="w-full h-full flex items-center justify-center text-sm font-bold text-gray-400">
                              {u.name?.[0]?.toUpperCase()}
                            </div>
                          )}
                        </div>
                        <div className="min-w-0">
                          <div className="font-semibold text-gray-800 truncate max-w-[160px] flex items-center gap-1">
                            {u.name}
                            {u.verificationStatus === "aprobado" && (
                              <BadgeCheck size={15} className="text-blue-500 flex-shrink-0" title="Identidad verificada" />
                            )}
                          </div>
                          <div className="text-xs text-gray-400 truncate max-w-[160px]">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    {/* Phone */}
                    <td className="px-4 py-3 hidden md:table-cell">
                      <span className="text-gray-600 text-xs font-mono">
                        {u.phone ? `${u.phoneCountryCode ?? ""} ${u.phone}` : <span className="text-gray-300">—</span>}
                      </span>
                    </td>
                    {/* Role */}
                    <td className="px-4 py-3">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${ROLE_COLORS[u.role] ?? "bg-gray-100 text-gray-600"}`}>
                        {u.role}
                      </span>
                    </td>
                    {/* QPoints */}
                    <td className="px-4 py-3 hidden lg:table-cell">
                      <span className="text-amber-600 font-semibold text-sm">{u.qpoints ?? 0}</span>
                    </td>
                    {/* Verifications */}
                    <td className="px-4 py-3 hidden sm:table-cell">
                      <div className="flex flex-col gap-0.5">
                        <CheckBadge ok={u.emailVerified} label="Email" />
                        <CheckBadge ok={u.whatsappVerified} label="WhatsApp" />
                        {u.documentPhoto && (
                          <div className="flex items-center gap-1.5 mt-1">
                            <span className={`text-[11px] font-semibold ${u.verificationStatus === "aprobado" ? "text-blue-600" : u.verificationStatus === "rechazado" ? "text-red-500" : "text-amber-600"}`}>
                              Doc: {u.verificationStatus === "aprobado" ? "Verificado" : u.verificationStatus === "rechazado" ? "Rechazado" : "Pendiente"}
                            </span>
                            <button
                              onClick={() => setVerifyUser(u)}
                              className="px-2 py-0.5 rounded-md text-[11px] font-semibold bg-blue-50 text-blue-600 border border-blue-200 hover:bg-blue-100 transition"
                            >
                              Validar
                            </button>
                          </div>
                        )}
                      </div>
                    </td>
                    {/* Registration date */}
                    <td className="px-4 py-3 hidden xl:table-cell">
                      <div className="text-xs text-gray-500">
                        {dayjs(u.createdAt).format("DD MMM YYYY")}
                      </div>
                      <div className="text-xs text-gray-400">{dayjs(u.createdAt).fromNow()}</div>
                    </td>
                    {/* isActive badge */}
                    <td className="px-4 py-3">
                      {u.isActive !== false ? (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-700">
                          <Wifi size={10} /> Activo
                        </span>
                      ) : (
                        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold bg-red-100 text-red-600">
                          <WifiOff size={10} /> Inactivo
                        </span>
                      )}
                    </td>
                    {/* Action */}
                    <td className="px-4 py-3">
                      <button
                        onClick={() => setProfileId(u.id)}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-50 hover:bg-blue-100 text-blue-600 rounded-lg text-xs font-semibold transition border border-blue-200"
                      >
                        <Eye size={13} /> Ver perfil
                      </button>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {pages > 1 && (
          <div className="flex items-center justify-between px-4 py-3 border-t bg-gray-50">
            <span className="text-xs text-gray-500">
              Página {page} de {pages} · {total.toLocaleString()} usuarios
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => fetchUsers(page - 1, search)}
                disabled={page <= 1 || loading}
                className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronLeft size={16} />
              </button>
              {Array.from({ length: Math.min(5, pages) }, (_, i) => {
                const p = page <= 3 ? i + 1 : page - 2 + i;
                if (p < 1 || p > pages) return null;
                return (
                  <button
                    key={p}
                    onClick={() => fetchUsers(p, search)}
                    className={`w-7 h-7 rounded-lg text-xs font-semibold transition ${p === page ? "bg-blue-500 text-white" : "hover:bg-gray-200 text-gray-600"}`}
                  >
                    {p}
                  </button>
                );
              })}
              <button
                onClick={() => fetchUsers(page + 1, search)}
                disabled={page >= pages || loading}
                className="p-1.5 rounded-lg hover:bg-gray-200 disabled:opacity-40 disabled:cursor-not-allowed transition"
              >
                <ChevronRight size={16} />
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Profile Drawer */}
      {verifyUser && (
        <UserVerificationModal
          user={verifyUser}
          onClose={() => setVerifyUser(null)}
          onUpdated={() => fetchUsers(page, search)}
        />
      )}

      {profileId && (
        <UserProfileDrawer
          userId={profileId}
          onClose={() => setProfileId(null)}
          onUpdated={() => fetchUsers(page, search)}
        />
      )}
    </div>
  );
}
